import { Box, Stack } from '@mui/material';
import {
  AddCircleOutline,
  Chat,
  ShoppingBasket,
  Work,
} from '@mui/icons-material';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { useMuiSwitchableTheme } from '@farmshare/ui-components';
import { userState } from 'state';
import { useCognitoAuth } from 'features/auth';
import { useAuthStorageCleanup } from 'lib/hooks/use-auth-storage-cleanup';
import { isBuyerOnly } from 'lib/userHelpers';

import { useProducerNav } from '../nav/hooks/use-producer-nav';
import {
  buildProducerNavConfig,
  ProducerNavModule,
} from '../nav/producer-nav-config';
import { IconRail } from '../nav/icon-rail';
import { SecNavPanel } from '../nav/sec-nav-panel';
import { MobileTopBar } from '../nav/mobile-top-bar';
import { MobileDrawer } from '../nav/mobile-drawer';

interface ProducerShellProps {
  children: ReactNode;
}

function ProducerShellInner({ children }: ProducerShellProps) {
  const navigate = useNavigate();
  const user = useRecoilValue(userState);
  const { logout } = useCognitoAuth();
  const { markLogout } = useAuthStorageCleanup();

  const handleLogout = useCallback(async () => {
    markLogout();
    await logout();
    navigate('/login');
  }, [logout, markLogout, navigate]);

  const buyerOnly = user ? isBuyerOnly(user) : false;

  const modules = useMemo(
    () =>
      buildProducerNavConfig(
        {
          requestJob: <AddCircleOutline />,
          jobs: <Work />,
          chat: <Chat />,
          orders: <ShoppingBasket />,
        },
        { buyerOnly },
      ),
    [buyerOnly],
  );

  const nav = useProducerNav(modules);

  // Sync producer nav width with theme breakpoints.
  // IconRail is 64px (collapsed left nav); on mobile there's no sidebar.
  const { setNavMode, setNavCollapsed } = useMuiSwitchableTheme();
  useEffect(() => {
    if (nav.isMobile) {
      setNavMode('top');
      setNavCollapsed(true);
    } else {
      setNavMode('left');
      setNavCollapsed(true);
    }
  }, [nav.isMobile, setNavMode, setNavCollapsed]);

  const visibleModuleData: ProducerNavModule | undefined = useMemo(
    () => modules.find((m) => m.id === nav.visibleModule),
    [modules, nav.visibleModule],
  );

  const handleClickModule = (mod: ProducerNavModule) => {
    if (mod.href) {
      navigate(mod.href);
    }
  };

  return (
    <Stack
      direction="row"
      sx={{
        height: '100vh',
        p: { xs: 0, md: 1.5 },
        bgcolor: 'primary.main',
      }}
    >
      {/* Desktop nav */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <IconRail
          modules={modules}
          activeModule={nav.activeModule}
          hoveredModule={nav.hoveredModule}
          isSecNavVisible={nav.isSecNavVisible}
          onHoverModule={nav.setHoveredModule}
          onClickModule={handleClickModule}
          userName={user?.first_name || undefined}
          onLogout={handleLogout}
        />
        <SecNavPanel
          module={visibleModuleData}
          isVisible={nav.isSecNavVisible}
          isHovered={nav.hoveredModule !== null}
          isPinned={nav.isPinned}
          onTogglePin={nav.togglePin}
          onHover={nav.setHoveredModule}
        />
      </Box>

      {/* Mobile nav */}
      <MobileTopBar
        onOpenDrawer={nav.toggleMobileDrawer}
        height={nav.MOBILE_TOP_BAR_HEIGHT}
      />
      <MobileDrawer
        open={nav.isMobileDrawerOpen}
        onClose={nav.closeMobileDrawer}
        modules={modules}
        activeModule={nav.activeModule}
      />

      {/* Content area */}
      <Box
        component="main"
        sx={{
          bgcolor: 'background.default',
          flexGrow: 1,
          minWidth: 0,
          overflow: 'auto',
          pt: { xs: `${nav.MOBILE_TOP_BAR_HEIGHT + 24}px`, md: 4 },
          px: { xs: 1.5, md: 3 },
          pb: { xs: 1.5, md: 3 },
          transition: 'margin-left 0.3s ease',
          ml: { xs: 0, md: 1.5 },
          borderRadius: { xs: 0, md: 2 },
          '@media print': {
            pt: 0,
          },
        }}
      >
        <Box sx={{ maxWidth: { md: 1200 }, mx: { md: 'auto' } }}>
          {children}
        </Box>
      </Box>
    </Stack>
  );
}

export function ProducerShell({ children }: ProducerShellProps) {
  return <ProducerShellInner>{children}</ProducerShellInner>;
}
